import { ThemeProvider } from '@emotion/react';
import { Grid, Modal } from '@mui/material';
import Box from '@mui/material/Box';
import { getCroppedImg, updateImagePreview } from 'APIs/utils';
import { useAppSelector } from 'app/hooks';
import CopyToClipButton from 'components/atoms/Button/CopyToClipButton';
import NormalButton from 'components/atoms/Button/NormalButton';
import {
  HeaderUserDropZoneCards,
  ResponsiveUserDropZoneCards,
} from 'components/molecules/Card/DropZone';
import { selectAuth, updateProfilePicture } from 'features/auth/authSlice';
import { UserProfileData } from 'interfaces/UserData.interface';
import React, { useEffect, useRef, useState } from 'react';
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/pagination';
import {
  BackgroundBox,
  HeaderBar,
  Indicator,
  Layer1,
  ParaText,
  ProfileImg,
  TabsWrapper,
  colortheme,
} from './styled';

const OtherProfileHeader: React.FC<{
  setCurrentTab: any;
  currentTab: string;
  handleSubmit: () => void;
  editing: boolean;
  data: UserProfileData;
  setData: React.Dispatch<React.SetStateAction<UserProfileData>>;
  fetchData: () => void;
  toggleEditing: () => void;
}> = ({
  currentTab,
  setCurrentTab,
  editing,
  data,
  setData,
  handleSubmit,
  fetchData,
  toggleEditing,
}) => {
  const location = useLocation();
  const authState = useAppSelector(selectAuth);

  const useravatar = require('assets/Profile/buku_default_avatar.png');
  const profileHeadBack = require('assets/Profile/buku_default_banner.png');
  const penEditIcon = require('assets/icons/pen-edit.svg');

  const [imageUrl, setImageUrl] = React.useState<any>(data.avatar || '');
  const [bannerUrl, setBannerUrl] = React.useState<any>(data.banner || '');
  const [isBanner, setIsBanner] = useState<boolean>(false);
  const [isAvatar, setIsAvatar] = useState<boolean>(false);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const [crop, setCrop] = React.useState<Crop>();
  const [currentCroppingImage, setCurrentCroppingImage] = useState<File>();
  const [image, setImage] = useState<File>();
  const [currentCroppingKey, setCurrentCroppingKey] = useState<string>('');

  const imgRef = useRef<HTMLImageElement>(null);
  const dispatch = useDispatch();

  const SetUserAvatar = (url: any): void => {
    setIsAvatar(true);
    if (url && url !== '') {
      setImageUrl(url);
    } else {
      setImageUrl('');
    }
  };

  const SetUserHeader = (url: any): void => {
    setIsBanner(true);
    if (url && url !== '') {
      setBannerUrl(url);
    } else {
      setBannerUrl('');
    }
  };

  const tab = new URLSearchParams(useLocation().search).get('tab');

  React.useEffect(() => {
    setImageUrl(data.avatar || '');
    setBannerUrl(data.banner || '');
    // eslint-disable-next-line
  }, [data]);

  const applyImageCrop = async () => {
    if (!currentCroppingImage || !crop || !currentCroppingKey) return;
    const file = await getCroppedImg(
      currentCroppingImage,
      crop,
      currentCroppingImage?.name,
      imgRef
    );
    if (file) {
      setImage(file);
      SetUserAvatar(URL.createObjectURL(file));
      dispatch(updateProfilePicture(URL.createObjectURL(file)));
      setData({
        ...data,
        ...(isAvatar && { avatar: URL.createObjectURL(file) }),
        ...(isBanner && { banner: URL.createObjectURL(file) }),
      });
      setCrop(undefined);
      setCurrentCroppingImage(undefined);
      setCurrentCroppingKey('');
      setIsAvatar(false);
      setIsBanner(false);
    }
  };

  useEffect(() => {
    if (image) {
      setCurrentCroppingKey('file');
      setCurrentCroppingImage(image);
    }
  }, [image]);

  return (
    <React.Fragment>
      <ThemeProvider theme={colortheme}>
        <HeaderBar>
          <BackgroundBox>
            {editing ? (
              <HeaderUserDropZoneCards
                image={data.banner}
                setImage={setImage}
                SetHeader={SetUserHeader}
              />
            ) : (
              <img
                src={bannerUrl ? bannerUrl : profileHeadBack}
                alt="header back"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            )}
          </BackgroundBox>
          <Layer1 container>
            <Grid
              item
              xs={6}
              style={{
                display: 'flex',
                justifyContent: 'start',
                alignItems: 'center',
              }}
            >
              {editing ? (
                <ResponsiveUserDropZoneCards
                  setImage={setImage}
                  SetAvatar={SetUserAvatar}
                  image={data.avatar}
                />
              ) : (
                <ProfileImg
                  src={imageUrl ? imageUrl : useravatar}
                  alt="header back"
                />
              )}
            </Grid>
            <Grid
              item
              xs={6}
              style={{
                display: 'flex',
                justifyContent: 'end',
                alignItems: 'center',
              }}
            >
              {authState?.username === data.username && (
                <ParaText
                  onClick={() => {
                    editing ? handleSubmit() : toggleEditing();
                  }}
                  style={{ cursor: 'pointer', zIndex: '100' }}
                >
                  <img
                    src={penEditIcon.default}
                    alt="header back"
                    style={{
                      width: '15px',
                      height: '15px',
                      marginRight: '5px',
                      zIndex: '2',
                    }}
                  />
                  {editing ? 'Save Edited Changes ' : 'Profile Edit'}
                </ParaText>
              )}
            </Grid>
          </Layer1>
        </HeaderBar>

        <Grid container>
          <Grid
            item
            xs={2.5}
            sx={{
              display: 'flex',
              justifyContent: 'start',
              flexDirection: 'column',
              padding: '1rem 3rem',
              [colortheme.breakpoints.down('lg')]: {
                display: 'none',
              },
            }}
          >
            {authState.tatumWalletAddress && (
              <CopyToClipButton
                text={authState.tatumWalletAddress || ''}
                style={{ width: '100%' }}
              />
            )}
          </Grid>
          <Grid
            item
            xs={12}
            lg={9.5}
            sx={{
              padding: '1rem 3rem',
              [colortheme.breakpoints.down('md')]: {
                padding: '1rem',
              },
            }}
          >
            <TabsWrapper>
              <Box
                style={{
                  display: 'flex',
                  gap: '5px',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                <Link to={'?tab=Feed'} style={{ textDecoration: 'none' }}>
                  <NormalButton
                    color={`${
                      tab === 'Feed' || !tab ? 'yellow' : 'transparent'
                    }`}
                    style={{ width: '150px' }}
                  >
                    Feed
                  </NormalButton>
                </Link>
                <Link to={'?tab=Collected'} style={{ textDecoration: 'none' }}>
                  <NormalButton
                    style={{ width: '150px' }}
                    color={tab === 'Collected' ? 'yellow' : 'transparent'}
                  >
                    Collected
                  </NormalButton>
                </Link>
                <Link to={'?tab=Collectors'} style={{ textDecoration: 'none' }}>
                  <NormalButton
                    color={`${tab === 'Collectors' ? 'yellow' : 'transparent'}`}
                    style={{ width: '150px' }}
                  >
                    Collectors
                    <Indicator
                      color={
                        location.pathname.toLowerCase().indexOf('collectors') <=
                        0
                          ? 'white'
                          : undefined
                      }
                    >
                      {data.subscribers ? data.subscribers.length : 0}
                    </Indicator>
                  </NormalButton>
                </Link>

                <NormalButton
                  color={`${tab === 'Splits' ? 'yellow' : 'transparent'}`}
                  style={{ width: '150px', color: '#252825' }}
                  isDisabled={true}
                >
                  Splits
                </NormalButton>
                <Link
                  to={'?tab=Backstage'}
                  style={{
                    textDecoration: 'none',
                    pointerEvents: data.canPost ? 'auto' : 'none',
                  }}
                >
                  <NormalButton
                    isDisabled={!data.canPost}
                    color={`${tab === 'Backstage' ? 'yellow' : 'transparent'}`}
                    style={{ width: '150px' }}
                  >
                    Backstage
                  </NormalButton>
                </Link>
                <Link to={'?tab=Badges'} style={{ textDecoration: 'none' }}>
                  <NormalButton
                    color={`${tab === 'Badges' ? 'yellow' : 'transparent'}`}
                    style={{ width: '150px', color: '#252825' }}
                    isDisabled
                  >
                    Badges
                  </NormalButton>
                </Link>
              </Box>
            </TabsWrapper>
          </Grid>
          <Modal
            open={!!currentCroppingImage}
            onClose={() => setCurrentCroppingImage(undefined)}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                borderRadius: '10px',
                backgroundColor: '#fff',
                padding: '10px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexDirection: 'column',
                }}
              >
                <ReactCrop
                  crop={crop}
                  onChange={c => setCrop(c)}
                  onComplete={c =>
                    updateImagePreview(c, imgRef, previewCanvasRef)
                  }
                  style={{
                    width: 'fit-content',
                    height: '40vh',
                  }}
                >
                  <img
                    src={
                      currentCroppingImage &&
                      URL.createObjectURL(currentCroppingImage)
                    }
                    ref={imgRef}
                    alt="To crop"
                    style={{
                      height: '40vh',
                    }}
                  />
                </ReactCrop>

                <canvas
                  ref={previewCanvasRef}
                  style={{
                    border: '1px dashed black',
                    objectFit: 'contain',
                    width: '45vw',
                    height: '40vh',
                    margin: '15px 0px',
                  }}
                />
              </Box>

              <NormalButton color="yellow" onClick={() => applyImageCrop()}>
                Crop
              </NormalButton>
            </Box>
          </Modal>
        </Grid>
      </ThemeProvider>
    </React.Fragment>
  );
};

export default OtherProfileHeader;
